import { Notifier } from '@airbrake/browser'
import React from 'react'

import { shouldIgnoreNotice } from './ignored-errors'

type FallbackType =
  | React.ReactNode
  | ((error: Error, info: React.ErrorInfo | null) => React.ReactNode)

type AirbrakeErrorBoundaryProps = {
  children: React.ReactNode
  fallback?: FallbackType
  onError?: (error: Error, info: React.ErrorInfo) => void
  userId: string | null
  cartId: string | undefined
}

type AirbrakeErrorBoundaryState = {
  hasError: boolean
  error: Error | null
  info: React.ErrorInfo | null
}

class AirbrakeErrorBoundary extends React.Component<
  AirbrakeErrorBoundaryProps,
  AirbrakeErrorBoundaryState
> {
  // Only defined if valid environment variables are present
  private airbrake?: Notifier

  constructor(props: AirbrakeErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, info: null }
    const projectIdStr = process.env.NEXT_PUBLIC_AIRBRAKE_PROJECT_ID
    const projectKey = process.env.NEXT_PUBLIC_AIRBRAKE_PROJECT_KEY
    const projectIdNum = projectIdStr ? Number(projectIdStr) : NaN
    const hasValidConfig =
      !!projectKey && !!projectIdStr && !Number.isNaN(projectIdNum) && projectIdNum > 0

    if (hasValidConfig) {
      this.airbrake = new Notifier({
        projectId: projectIdNum,
        projectKey: projectKey!,
        environment: process.env.NEXT_PUBLIC_API_ENV,
      })

      this.airbrake.addFilter((notice) => {
        // Drop known-noise notices (hydration mismatches, browser-extension
        // errors, scanner probes) before they consume Airbrake quota.
        if (shouldIgnoreNotice(notice)) return null

        const { userId, cartId } = this.props

        if (userId) notice.context.user_id = userId
        if (cartId) notice.context.cart_id = cartId

        return notice
      })
    }
  }

  static getDerivedStateFromError(error: Error): AirbrakeErrorBoundaryState {
    return { hasError: true, error, info: null }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Save info so a functional fallback can render context if desired
    this.setState({ info })

    // Notify Airbrake
    this.airbrake?.notify({
      error,
      params: { info },
    })

    // Optional user-provided handler (e.g., toast)
    if (this.props.onError) this.props.onError(error, info)
  }

  render(): React.ReactNode {
    const { hasError, error, info } = this.state
    const { fallback } = this.props

    if (hasError) {
      if (typeof fallback === 'function')
        return (fallback as (e: Error, i: React.ErrorInfo | null) => React.ReactNode)(
          error as Error,
          info,
        )
      if (fallback) return fallback
      // Default: render nothing so the boundary is non-blocking by default
      return null
    }
    return this.props.children
  }
}

export default AirbrakeErrorBoundary
